/**
 * The parts of runtime i18n that are the same everywhere.
 *
 * Two runtimes sit on top of this — dashboard/js/i18n.js for the signed-in app
 * and site/js/public-i18n.js for the share pages and the OAuth consent screen —
 * and they differ in exactly one thing: how the locale is chosen. The app also
 * weighs the arrival language and the signed-out auth locale, and reconciles
 * against the account's `profiles.ui_language`; the public pages ask the browser
 * and offer a picker. Everything under that decision — which locales exist, how
 * a code is clamped to one, where a bundle lives, what t() does with `{vars}`,
 * how `data-i18n` markup is filled in — was written twice and drifted.
 *
 * So it is written once here, and each runtime keeps only its own chain.
 *
 * Also `GHD.trOr(key, fallback, vars)`: t() with an English literal for when
 * there is no translation. Four files had a copy of it and one had drifted into
 * printing the raw key.
 *
 * Extends the `window.GHD` namespace. Load after the generated bundle loader
 * (which defines `__GHD_LOCALES__` and the message globals) and before the
 * runtime that uses it.
 */
(function () {
  window.GHD = window.GHD || {};

  var DEFAULT = "en";

  /**
   * The shipped UI locales, from the generated bundle (i18n/locales.json).
   *
   * Read on every call rather than captured at load: this file has no way to
   * insist it evaluates after the bundle loader on every page, and a captured
   * `["en"]` would silently clamp every other language away.
   */
  function locales() {
    return window.__GHD_LOCALES__ || [{ code: DEFAULT, native: "English", english: "English" }];
  }

  /** The message globals the per-locale bundle defines, or null. */
  function bundle(loc) {
    return window["__GHD_MSG_" + String(loc).toUpperCase() + "__"] || null;
  }

  /** Fill `{name}` placeholders from `vars`; an unknown one is left as written. */
  function interpolate(text, vars) {
    if (!vars) return text;
    return String(text).replace(/\{(\w+)\}/g, function (whole, name) {
      return Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : whole;
    });
  }

  /**
   * Map any language code — including the ~50 a chat reply can be written in —
   * to a shipped UI locale, or null.
   */
  function toSupported(code) {
    if (!code) return null;
    var primary = String(code).replace("_", "-").split("-")[0].toLowerCase();
    var supported = locales().map(function (entry) {
      return entry.code;
    });
    return supported.indexOf(primary) !== -1 ? primary : null;
  }

  /**
   * The public tag a locale is written under — `<html lang>` and every Intl
   * call. The code itself unless the registry names one (`zh` → `zh-Hant`: a
   * bare "zh" lets the browser and Intl fall back to Simplified).
   */
  function bcp47(code) {
    var list = locales();
    for (var i = 0; i < list.length; i++) {
      if (list[i].code === code) return list[i].bcp47 || code;
    }
    return code;
  }

  /**
   * The tag an Intl date formatter is handed: a public tag, on the Gregorian
   * calendar. Thai's default is the Buddhist one, which prints 1990 as 2533, and
   * a birth year has to read as the year the reader typed. A tag already on the
   * Gregorian calendar comes back as it was.
   */
  function intlTag(tag) {
    try {
      if (tag && new Intl.DateTimeFormat(tag).resolvedOptions().calendar !== "gregory") return tag + "-u-ca-gregory";
    } catch (e) {
      /* a tag Intl refuses is the caller's to handle, unchanged */
    }
    return tag;
  }

  /**
   * The active message table for a locale.
   *
   * Each bundle is already merged over the English base at build time; the
   * English one is laid down first anyway, to cover a bundle that failed to
   * load or a loader that disagrees with the runtime about the locale.
   */
  function messagesFor(loc) {
    return Object.assign({}, bundle(DEFAULT) || {}, bundle(loc) || {});
  }

  /**
   * A `t(key, vars)` over a table the caller can swap under it.
   *
   * A missing key answers with the key itself: a visible signal, and what lets
   * GHD.trOr tell a miss from a translation. `t.setMessages(next)` replaces the
   * table in place, so the language switch the dashboard does without reloading
   * reaches every reference already handed out — `window.t` included.
   */
  function makeT(messages) {
    var table = messages || {};
    function t(key, vars) {
      var text = Object.prototype.hasOwnProperty.call(table, key) ? table[key] : key;
      return interpolate(text, vars);
    }
    t.setMessages = function (next) {
      table = next || {};
    };
    return t;
  }

  /**
   * Translate static markup: `data-i18n` (innerHTML) and
   * `data-i18n-attr="attr:key;attr2:key2"` (attribute values). Call after
   * rendering, with the runtime's own `t`.
   */
  function applyTranslations(root, t) {
    var scope = root || document;
    scope.querySelectorAll("[data-i18n]").forEach(function (el) {
      el.innerHTML = t(el.getAttribute("data-i18n"));
    });
    scope.querySelectorAll("[data-i18n-attr]").forEach(function (el) {
      el.getAttribute("data-i18n-attr")
        .split(";")
        .forEach(function (pair) {
          var at = pair.indexOf(":");
          if (at === -1) return;
          el.setAttribute(pair.slice(0, at).trim(), t(pair.slice(at + 1).trim()));
        });
    });
  }

  GHD.i18n = {
    DEFAULT: DEFAULT,
    // Marks `ghd_ui_lang` as a deliberate choice rather than a value a localized
    // landing page seeded in passing (site/js/head-init.js leaves ours alone).
    // Both pickers write it, and they mean the same thing by it.
    SEED_APP: "app",
    locales: locales,
    bundle: bundle,
    toSupported: toSupported,
    bcp47: bcp47,
    intlTag: intlTag,
    messagesFor: messagesFor,
    makeT: makeT,
    applyTranslations: applyTranslations,
    interpolate: interpolate,
  };

  /**
   * t(), with the English words to print when there is no translation.
   *
   * Two cases produce the same answer here: a page that carries no i18n runtime
   * at all (the shared conversation view loads cards.js without one), and a key
   * the catalog has not got. t() answers a miss with the key, so that is what
   * this watches for — and the fallback gets the same `{var}` interpolation the
   * translation would have had.
   *
   * A fallback is a safety net, never the copy: the catalog wins wherever it has
   * an entry, so an edit to the English wording belongs in i18n/en/*.json.
   */
  GHD.trOr = function (key, fallback, vars) {
    if (typeof window.t === "function") {
      var translated = window.t(key, vars);
      if (translated !== key) return translated;
    }
    return interpolate(fallback == null ? key : fallback, vars);
  };
})();
